import type { ComponentType, SVGProps } from 'react';

import { cn } from '@/lib/utils';

import ActivityCentre from '../ActivityCentre';
import { isLinkActive } from '../Sidebar/active';
import { ICON_MAP } from '../Sidebar/iconMap';
import type { SidebarUser } from '../Sidebar/SidebarUserProfile';
import type { LinkItem, NavConfig, NavItem } from '../Sidebar/types';
import {
  ACTIVE_ROW_CLASS,
  DISABLED_ITEM_CLASS,
  HOVER_ROW_CLASS,
  ITEM_ROW_CLASS,
  NAV_ICON_CLASS,
  SECTION_LABEL_CLASS,
} from './constants';
import { MobileCountBadge } from './MobileCountBadge';
import { MobileSidebarExpandable } from './MobileSidebarExpandable';
import { MobileSidebarHeader } from './MobileSidebarHeader';

export interface MobileSidebarProps {
  // Pre-resolved sections — the caller applies capability filtering (see
  // selectVisibleNav in ../Sidebar) before passing the config down.
  nav: NavConfig;
  user: SidebarUser;
  // Current location, for active-row derivation (controlled, like Sidebar).
  pathname: string;
  // Badge counts keyed by item label; 0 renders the muted zero badge.
  counts?: Readonly<Record<string, number>>;
  // Labels of expandables that start open (in addition to active-derived ones).
  defaultExpanded?: readonly string[];
  onClose: () => void;
}

interface RowContentProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  count: number | undefined;
}

function RowContent({ icon: Icon, label, count }: RowContentProps) {
  return (
    <>
      <Icon className={NAV_ICON_CLASS} aria-hidden="true" />
      <span className="flex-1">{label}</span>
      <MobileCountBadge count={count} />
    </>
  );
}

function LinkRow({
  item,
  pathname,
  count,
  onClose,
}: {
  item: LinkItem;
  pathname: string;
  count: number | undefined;
  onClose: () => void;
}) {
  const active = isLinkActive(item, pathname);
  return (
    <a
      href={item.href}
      data-nav-active={active || undefined}
      data-turbo-frame="_top"
      onClick={onClose}
      className={cn(
        ITEM_ROW_CLASS,
        HOVER_ROW_CLASS,
        active && ACTIVE_ROW_CLASS,
        item.disabled && DISABLED_ITEM_CLASS,
      )}
    >
      <RowContent icon={ICON_MAP[item.icon]} label={item.label} count={count} />
    </a>
  );
}

function navItemKey(item: NavItem, index: number): string {
  switch (item.kind) {
    case 'link':
      return `link-${item.label}-${item.href}-${index}`;
    case 'expandable':
      return `expandable-${item.label}-${index}`;
    case 'react-mount':
      return `react-${item.component}-${index}`;
  }
}

// Mobile navigation drawer panel (Figma node 14763-95035, GRAIN-2331). Pure
// panel: open/closed state and the drawer/overlay shell live in
// MobileSidebarDrawer. Uses the same NAV_CONFIG + selectVisibleNav as desktop.
export function MobileSidebar({
  nav,
  user,
  pathname,
  counts,
  defaultExpanded,
  onClose,
}: MobileSidebarProps) {
  const renderItem = (item: NavItem, index: number) => {
    switch (item.kind) {
      case 'link':
        return (
          <LinkRow
            item={item}
            pathname={pathname}
            count={counts?.[item.label]}
            onClose={onClose}
          />
        );
      case 'react-mount':
        return (
          <ActivityCentre
            triggerClassName={cn(ITEM_ROW_CLASS, HOVER_ROW_CLASS)}
            iconClassName={NAV_ICON_CLASS}
            renderCount={(count) => <MobileCountBadge count={count} />}
          />
        );
      case 'expandable':
        return (
          <MobileSidebarExpandable
            item={item}
            pathname={pathname}
            defaultExpanded={defaultExpanded?.includes(item.label) ?? false}
            onClose={onClose}
          />
        );
    }
  };

  return (
    <div className="flex h-full w-full flex-col bg-off-white">
      <MobileSidebarHeader user={user} onClose={onClose} />
      <nav
        aria-label="Main navigation"
        className="flex flex-1 flex-col gap-8 overflow-y-auto overscroll-contain px-6 pb-8 pt-6"
      >
        {nav.map((section, sectionIndex) => (
          <section
            key={section.heading ?? `section-${sectionIndex}`}
            aria-labelledby={section.heading ? `mobile-nav-${section.heading}` : undefined}
            className="flex flex-col gap-3"
          >
            {section.heading && (
              <h2 id={`mobile-nav-${section.heading}`} className={SECTION_LABEL_CLASS}>
                {section.heading}
              </h2>
            )}
            <ul className="flex flex-col gap-2">
              {section.items.map((item, index) => (
                <li key={navItemKey(item, index)}>{renderItem(item, index)}</li>
              ))}
            </ul>
          </section>
        ))}
      </nav>
    </div>
  );
}

export default MobileSidebar;